// 持久化存储服务 - 生产环境使用API，开发环境使用localStorage

interface TestCode {
  id: string;
  code: string;
  isUsed: boolean;
  usedAt?: string;
  createdAt: string;
  status?: 'available' | 'distributed' | 'used';
  source?: 'manual' | 'auto';
  assessmentType?: 'emotional-memory-pattern' | 'big-five-personality' | '34-talent-test'; // 新增：测评类型
}

// 检查是否为生产环境
const isProduction = process.env.NODE_ENV === 'production';

// API基础URL
const API_BASE = isProduction ? 'https://personality-vector-lab.pages.dev' : '';

// 测评码相关API
export const testCodeStorage = {
  // 获取所有测评码
  async getTestCodes(): Promise<TestCode[]> {
    if (!isProduction) {
      // 开发环境使用localStorage
      const codes = localStorage.getItem('adminTestCodes');
      return codes ? JSON.parse(codes) : [];
    }

    try {
      const response = await fetch(`${API_BASE}/api/testcodes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('获取测评码失败');
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) {
      console.error('获取测评码失败:', error);
      return [];
    }
  },

  // 保存测评码
  async saveTestCodes(testCodes: TestCode[]): Promise<boolean> {
    if (!isProduction) {
      // 开发环境使用localStorage
      localStorage.setItem('adminTestCodes', JSON.stringify(testCodes));
      return true;
    }

    try {
      const response = await fetch(`${API_BASE}/api/testcodes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ testCodes }),
      });

      if (!response.ok) {
        throw new Error('保存测评码失败');
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('保存测评码失败:', error);
      return false;
    }
  },

  // 更新测评码状态
  async updateTestCode(codeId: string, updates: Partial<TestCode>): Promise<boolean> {
    if (!isProduction) {
      // 开发环境使用localStorage
      const codes = await this.getTestCodes();
      const updatedCodes = codes.map(code => 
        (code.id === codeId || code.code === codeId) ? { ...code, ...updates } : code
      );
      return this.saveTestCodes(updatedCodes);
    }

    try {
      const response = await fetch(`${API_BASE}/api/testcodes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ codeId, updates }),
      });

      if (!response.ok) {
        throw new Error('更新测评码失败');
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('更新测评码失败:', error);
      return false;
    }
  }
};

// 统计数据相关API
export const statsStorage = {
  // 获取访问统计
  async getStats(): Promise<{ 
    totalVisits: number; 
    totalCompletions: number; 
    totalTests: number;
    testsByType: {
    'emotional-memory-pattern': number;
    'big-five-personality': number;
    '34-talent-test': number;
  };
    completionsByType: {
    'emotional-memory-pattern': number;
    'big-five-personality': number;
    '34-talent-test': number;
  };
  }> {
    if (!isProduction) {
      // 开发环境使用localStorage
      const visits = localStorage.getItem('totalVisits');
      const emotionalTests = localStorage.getItem('totalTests_emotional-memory-pattern');
      const bigFiveTests = localStorage.getItem('totalTests_big-five-personality');
      const talentTests = localStorage.getItem('totalTests_34-talent-test');
      const emotionalCompletions = localStorage.getItem('totalCompletions_emotional-memory-pattern');
      const bigFiveCompletions = localStorage.getItem('totalCompletions_big-five-personality');
      const talentCompletions = localStorage.getItem('totalCompletions_34-talent-test');
      
      const emotionalTestsCount = emotionalTests ? parseInt(emotionalTests) : 0;
      const bigFiveTestsCount = bigFiveTests ? parseInt(bigFiveTests) : 0;
      const talentTestsCount = talentTests ? parseInt(talentTests) : 0;
      const emotionalCompletionsCount = emotionalCompletions ? parseInt(emotionalCompletions) : 0;
      const bigFiveCompletionsCount = bigFiveCompletions ? parseInt(bigFiveCompletions) : 0;
      const talentCompletionsCount = talentCompletions ? parseInt(talentCompletions) : 0;
      
      return { 
        totalVisits: visits ? parseInt(visits) : 0,
        totalCompletions: emotionalCompletionsCount + bigFiveCompletionsCount + talentCompletionsCount,
        totalTests: emotionalTestsCount + bigFiveTestsCount + talentTestsCount,
        testsByType: {
          'emotional-memory-pattern': emotionalTestsCount,
          'big-five-personality': bigFiveTestsCount,
          '34-talent-test': talentTestsCount
        },
        completionsByType: {
          'emotional-memory-pattern': emotionalCompletionsCount,
          'big-five-personality': bigFiveCompletionsCount,
          '34-talent-test': talentCompletionsCount
        }
      };
    }

    try {
      const response = await fetch(`${API_BASE}/api/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('获取统计数据失败');
      }

      const result = await response.json();
      return result.success ? result.data : { 
        totalVisits: 0, 
        totalCompletions: 0, 
        totalTests: 0,
        testsByType: {
          'emotional-memory-pattern': 0,
          'big-five-personality': 0,
          '34-talent-test': 0
        },
        completionsByType: {
          'emotional-memory-pattern': 0,
          'big-five-personality': 0,
          '34-talent-test': 0
        }
      };
    } catch (error) {
      console.error('获取统计数据失败:', error);
      return { 
        totalVisits: 0, 
        totalCompletions: 0, 
        totalTests: 0,
        testsByType: {
          'emotional-memory-pattern': 0,
          'big-five-personality': 0,
          '34-talent-test': 0
        },
        completionsByType: {
          'emotional-memory-pattern': 0,
          'big-five-personality': 0,
          '34-talent-test': 0
        }
      };
    }
  },

  // 记录进入测评次数
  async recordTestEntry(assessmentType: 'emotional-memory-pattern' | 'big-five-personality' | '34-talent-test' = 'emotional-memory-pattern'): Promise<void> {
    if (!isProduction) {
      // 开发环境使用localStorage
      const tests = localStorage.getItem(`totalTests_${assessmentType}`);
      const currentTests = tests ? parseInt(tests) : 0;
      localStorage.setItem(`totalTests_${assessmentType}`, (currentTests + 1).toString());
      return;
    }

    try {
      await fetch(`${API_BASE}/api/tests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assessmentType }),
      });
    } catch (error) {
      console.error('记录测评进入失败:', error);
    }
  },

  // 记录访问
  async recordVisit(): Promise<void> {
    if (!isProduction) {
      // 开发环境使用localStorage
      const visits = localStorage.getItem('totalVisits');
      const currentVisits = visits ? parseInt(visits) : 0;
      localStorage.setItem('totalVisits', (currentVisits + 1).toString());
      return;
    }

    try {
      await fetch(`${API_BASE}/api/stats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('记录访问失败:', error);
    }
  },

  // 记录测评完成
  async recordCompletion(assessmentType: 'emotional-memory-pattern' | 'big-five-personality' | '34-talent-test' = 'emotional-memory-pattern'): Promise<void> {
    if (!isProduction) {
      // 开发环境使用localStorage
      const completions = localStorage.getItem(`totalCompletions_${assessmentType}`);
      const currentCompletions = completions ? parseInt(completions) : 0;
      localStorage.setItem(`totalCompletions_${assessmentType}`, (currentCompletions + 1).toString());
      return;
    }

    try {
      await fetch(`${API_BASE}/api/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assessmentType }),
      });
    } catch (error) {
      console.error('记录测评完成失败:', error);
    }
  }
};

// 全局设置接口
interface GlobalSettings {
  referralEnabled: boolean;
  testModeOpen: {
    'emotional-memory-pattern': boolean;
    'big-five-personality': boolean;
    '34-talent-test': boolean;
  };
  lastUpdated: string;
}

// 全局设置管理
export const globalSettings = {
  async getReferralEnabled(): Promise<boolean> {
    if (!isProduction) {
      // 开发环境仍使用localStorage
      return localStorage.getItem('referralEnabled') === 'true';
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/settings`);
      if (!response.ok) {
        throw new Error('获取全局设置失败');
      }
      const settings: GlobalSettings = await response.json();
      return settings.referralEnabled;
    } catch (error) {
      console.error('获取推荐功能设置失败:', error);
      // 出错时返回默认值
      return false;
    }
  },

  async setReferralEnabled(enabled: boolean): Promise<boolean> {
    if (!isProduction) {
      // 开发环境仍使用localStorage
      localStorage.setItem('referralEnabled', enabled.toString());
      return true;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ referralEnabled: enabled }),
      });
      
      if (!response.ok) {
        throw new Error('更新全局设置失败');
      }
      
      return true;
    } catch (error) {
      console.error('设置推荐功能失败:', error);
      return false;
    }
  }
};

// 其他设置仍使用localStorage（这些不需要跨设备同步）
export const localSettings = {
  async getTestModeOpen(assessmentType: 'emotional-memory-pattern' | 'big-five-personality' | '34-talent-test' = 'emotional-memory-pattern'): Promise<boolean> {
    if (!isProduction) {
      // 开发环境仍使用localStorage
      return localStorage.getItem(`testModeOpen_${assessmentType}`) === 'true';
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/settings`);
      if (!response.ok) {
        throw new Error('获取全局设置失败');
      }
      const settings: GlobalSettings = await response.json();
      return settings.testModeOpen[assessmentType];
    } catch (error) {
       console.error('获取开放模式设置失败:', error);
       // 出错时返回默认值
       return false;
     }
   },

   async setTestModeOpen(isOpen: boolean, assessmentType: 'emotional-memory-pattern' | 'big-five-personality' | '34-talent-test' = 'emotional-memory-pattern'): Promise<boolean> {
    if (!isProduction) {
      // 开发环境仍使用localStorage
      localStorage.setItem(`testModeOpen_${assessmentType}`, isOpen.toString());
      return true;
    }
    
    try {
      // 先获取当前设置
      const currentResponse = await fetch(`${API_BASE}/api/settings`);
      let currentSettings: GlobalSettings;
      
      if (currentResponse.ok) {
        currentSettings = await currentResponse.json();
      } else {
        // 如果获取失败，使用默认设置
        currentSettings = {
          referralEnabled: false,
          testModeOpen: {
            'emotional-memory-pattern': false,
            'big-five-personality': false,
            '34-talent-test': false
          },
          lastUpdated: new Date().toISOString()
        };
      }
      
      // 更新开放模式设置
      const updatedSettings = {
        ...currentSettings,
        testModeOpen: {
          ...currentSettings.testModeOpen,
          [assessmentType]: isOpen
        }
      };
      
      const response = await fetch(`${API_BASE}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedSettings),
      });
      
      if (!response.ok) {
        throw new Error('更新全局设置失败');
      }
      
      return true;
    } catch (error) {
      console.error('设置开放模式失败:', error);
      return false;
    }
  },

  getReferralEnabled(): boolean {
    return localStorage.getItem('referralEnabled') === 'true';
  },

  setReferralEnabled(enabled: boolean): void {
    localStorage.setItem('referralEnabled', enabled.toString());
  }
};